import ClickAnalyticsCore from './core';
import { ClickAnalyticsConfig } from './types';

// Global instance
let globalAnalytics: ClickAnalyticsCore | null = null;

/**
 * Initialize analytics for any JavaScript framework
 * Inspired by Vercel Analytics inject() function
 * 
 * @example
 * ```javascript
 * import { inject } from '@click-chutney/clickanalytics';
 * 
 * inject({
 *   trackingId: 'your-tracking-id',
 *   debug: true
 * });
 * ```
 */
export function inject(config: Partial<ClickAnalyticsConfig> = {}): ClickAnalyticsCore | null {
  // Prevent multiple initializations
  if (globalAnalytics) {
    console.warn('[ClickAnalytics] Already initialized. Use reset() to reinitialize.');
    return globalAnalytics;
  }

  // Auto-detect tracking ID from environment or meta tags
  const trackingId = config.trackingId || 
    getTrackingIdFromEnvironment() ||
    getTrackingIdFromMeta();

  if (!trackingId) {
    if (config.debug) {
      console.warn(
        '[ClickAnalytics] No tracking ID found.\n' +
        'Provide trackingId in config or add a meta tag:\n' +
        '<meta name="clickanalytics-tracking-id" content="your-tracking-id">\n' +
        'Get your tracking ID from: https://clickchutney.com/dashboard'
      );
    }
    return null;
  }

  const fullConfig: ClickAnalyticsConfig = {
    trackingId,
    debug: config.debug || false,
    disableInDev: config.disableInDev !== undefined ? config.disableInDev : true,
    apiUrl: config.apiUrl,
    beforeSend: config.beforeSend
  };

  try {
    globalAnalytics = new ClickAnalyticsCore(fullConfig);

    // Make analytics available globally
    if (typeof window !== 'undefined') {
      (window as any).clickAnalytics = globalAnalytics;
      
      // Add global functions for script tag usage
      (window as any).caPV = (url?: string, title?: string) => globalAnalytics?.pageview(url, title);
      (window as any).caEV = (name: string, properties?: Record<string, any>) => globalAnalytics?.event(name, properties);
      (window as any).caID = (userId: string) => globalAnalytics?.identify(userId);
    }

    if (fullConfig.debug) {
      console.log('[ClickAnalytics] Inject initialized successfully');
    }

    return globalAnalytics;
  } catch (error) {
    if (fullConfig.debug) {
      console.error('[ClickAnalytics] Failed to initialize:', error);
    }
    return null;
  }
}

/**
 * Get the current analytics instance
 */
export function getAnalytics(): ClickAnalyticsCore | null {
  return globalAnalytics || (typeof window !== 'undefined' ? (window as any).clickAnalytics : null);
}

/**
 * Track a page view
 */
export function pageview(url?: string, title?: string): void {
  const analytics = getAnalytics();
  if (analytics) {
    analytics.pageview(url, title);
  } else {
    console.warn('[ClickAnalytics] Not initialized. Call inject() first.');
  }
}

/**
 * Track a custom event
 */
export function event(name: string, properties?: Record<string, any>): void {
  const analytics = getAnalytics();
  if (analytics) {
    analytics.event(name, properties);
  } else {
    console.warn('[ClickAnalytics] Not initialized. Call inject() first.');
  }
}

/**
 * Identify a user
 */
export function identify(userId: string): void {
  const analytics = getAnalytics();
  if (analytics) {
    analytics.identify(userId);
  } else {
    console.warn('[ClickAnalytics] Not initialized. Call inject() first.');
  }
}

/**
 * Manually flush queued events
 */
export async function flush(): Promise<void> {
  const analytics = getAnalytics();
  if (analytics) {
    await analytics.flush();
  } else {
    console.warn('[ClickAnalytics] Not initialized. Call inject() first.');
  }
}

/**
 * Reset analytics session
 */
export function reset(): void {
  if (globalAnalytics) {
    globalAnalytics.reset();
  }
  globalAnalytics = null;
  
  if (typeof window !== 'undefined') {
    delete (window as any).clickAnalytics;
    delete (window as any).caPV;
    delete (window as any).caEV;
    delete (window as any).caID;
  }
}

/**
 * Helper functions
 */
function getTrackingIdFromEnvironment(): string | undefined {
  if (typeof process !== 'undefined' && process.env) {
    return process.env.NEXT_PUBLIC_CLICKANALYTICS_ID ||
           process.env.NEXT_PUBLIC_CLICKCHUTNEY_ID ||
           process.env.CLICKANALYTICS_TRACKING_ID;
  }
  return undefined;
}

function getTrackingIdFromMeta(): string | undefined {
  if (typeof document !== 'undefined') {
    const metaTag = document.querySelector('meta[name="clickanalytics-tracking-id"]') as HTMLMetaElement;
    if (metaTag && metaTag.content) {
      return metaTag.content;
    }
    
    // Also check for clickchutney meta tag for compatibility
    const ccMetaTag = document.querySelector('meta[name="clickchutney-site-id"]') as HTMLMetaElement;
    if (ccMetaTag && ccMetaTag.content) {
      return ccMetaTag.content;
    }
  }
  return undefined;
}

// Auto-inject if meta tag is present (similar to Vercel Analytics)
if (typeof window !== 'undefined') {
  // Auto-initialize when DOM is ready
  const autoInit = () => {
    const metaId = getTrackingIdFromMeta();
    if (metaId && !globalAnalytics) {
      inject({ trackingId: metaId, debug: false });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
}